/* ==========================================================================
   Aire de Agua · Guía de tallas
   Custom element <adea-size-guide>.

   Reglas de negocio implementadas:
   1. Para cada medida se busca la primera talla cuyo máximo >= el valor
      ingresado. Esto resuelve los huecos entre rangos (ej. 88,5 de pecho
      cae entre S y M) redondeando hacia arriba, que es lo que menos
      devoluciones genera.
   2. La talla final es la MAYOR de las tallas obtenidas por cada medida.
      Es la regla escrita en el callout: "guíate por tu medida más grande".
      Copy y código son la misma lógica, así no se desalinean.

   Integración con la PDP ADEA (sections/main-product-adea.liquid):
   el botón "Añadir talla X" no llama a /cart/add.js por su cuenta — busca la
   pill de talla del selector real, la marca y envía el form de la PDP. Así
   hereda la variante con el color ya elegido, el estado de agotado que el
   controlador de la PDP recalcula por color, y el flujo de carrito del tema.

   La apertura/cierre del <dialog> vive en adea-pdp.js (mismo mecanismo del
   modal anterior): aquí no hay gestión de diálogo.
   ========================================================================== */

(function () {
  if (customElements.get('adea-size-guide')) return; // evita doble registro

  class AdeaSizeGuide extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-gt-data]');
      if (!dataEl) return;

      try {
        const raw = JSON.parse(dataEl.textContent);
        this.sizes = raw.sizes;
        this.measures = raw.measures.map(function (m) {
          return {
            key: m.key,
            label: m.label,
            min: m.min.map(Number),
            max: m.max.map(Number)
          };
        });
      } catch (e) {
        return; // sin datos válidos, la tabla estática sigue siendo útil
      }

      this.inputs = Array.from(this.querySelectorAll('[data-gt-input]'));
      this.form = this.querySelector('[data-gt-form]');
      this.result = this.querySelector('[data-gt-result]');
      this.error = this.querySelector('[data-gt-error]');

      this.bind();
    }

    bind() {
      const self = this;

      if (this.form) {
        this.form.addEventListener('submit', function (e) {
          e.preventDefault();
          self.calculate();
        });
      }

      // Recalcular en vivo si ya se mostró un resultado: evita que la
      // clienta vea una talla obsoleta después de corregir una medida.
      this.inputs.forEach(function (input) {
        input.addEventListener('input', function () {
          if (self.result && !self.result.hidden) self.calculate();
        });
      });

      const addBtn = this.querySelector('[data-gt-add]');
      if (addBtn) {
        addBtn.addEventListener('click', function () { self.addToCart(addBtn); });
      }
    }

    /* --- Cálculo --------------------------------------------------------- */

    // Índice de la talla que corresponde a un valor en una medida dada.
    sizeIndexFor(value, measure) {
      if (!Number.isFinite(value) || value <= 0) return null;
      for (let i = 0; i < measure.max.length; i++) {
        if (value <= measure.max[i]) return i;
      }
      return measure.max.length - 1; // por encima del rango: la talla mayor
    }

    readInputs() {
      const out = [];
      this.inputs.forEach(function (input) {
        const v = parseFloat(String(input.value).replace(',', '.'));
        out.push({ key: input.dataset.gtInput, value: Number.isFinite(v) ? v : null });
      });
      return out;
    }

    calculate() {
      const values = this.readInputs();
      const hits = [];

      for (const entry of values) {
        if (entry.value === null) continue;
        const measure = this.measures.find(function (m) { return m.key === entry.key; });
        if (!measure) continue;
        const idx = this.sizeIndexFor(entry.value, measure);
        if (idx !== null) hits.push({ idx: idx, label: measure.label, value: entry.value });
      }

      if (!hits.length) {
        this.showError('Ingresa al menos una medida en centímetros para calcular tu talla.');
        return;
      }

      this.hideError();

      // La mayor de las tallas obtenidas.
      let winner = hits[0];
      for (const h of hits) { if (h.idx > winner.idx) winner = h; }

      this.render(winner.idx, hits, winner);
    }

    render(index, hits, winner) {
      const size = this.sizes[index];

      const sizeEl = this.querySelector('[data-gt-size]');
      if (sizeEl) sizeEl.textContent = size;

      // Línea de detalle con los rangos de esa talla.
      const detail = this.measures.map(function (m) {
        return m.label + ' ' + m.min[index] + '–' + m.max[index];
      }).join(' · ');
      const detailEl = this.querySelector('[data-gt-detail]');
      if (detailEl) detailEl.textContent = detail;

      // Por qué esta talla y no otra: transparencia = menos dudas.
      const reasonEl = this.querySelector('[data-gt-reason]');
      if (reasonEl) {
        reasonEl.textContent = hits.length > 1
          ? 'Tomamos tu medida más grande: ' + winner.label.toLowerCase() + ' ' + winner.value + ' cm.'
          : '';
      }

      this.highlightColumn(index);
      this.updateCartButton(size);

      if (this.result) {
        this.result.hidden = false;
        this.result.setAttribute('tabindex', '-1');
        this.result.focus({ preventScroll: true });
      }
    }

    highlightColumn(index) {
      this.querySelectorAll('.is-match').forEach(function (cell) {
        cell.classList.remove('is-match');
      });
      // +1 porque la primera columna es la etiqueta de la medida.
      this.querySelectorAll('[data-gt-table] tr').forEach(function (row) {
        const cell = row.children[index + 1];
        if (cell) cell.classList.add('is-match');
      });
    }

    showError(msg) {
      if (!this.error) return;
      this.error.textContent = msg;
      this.error.hidden = false;
      if (this.result) this.result.hidden = true;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }

    /* --- Carrito: delega en la PDP ADEA ----------------------------------- */

    // Pill del selector de talla de la PDP para una talla dada. Se busca en
    // todas las pills no-color; el catálogo solo tiene una opción de talla,
    // y sus valores (S/M/L/XL) no colisionan con otras opciones.
    pillFor(size) {
      const root = this.closest('.adea-pdp');
      if (!root) return null;
      const pills = root.querySelectorAll('[data-adea-pill]');
      for (const pill of pills) {
        if (pill.dataset.value === size) return pill;
      }
      return null;
    }

    updateCartButton(size) {
      const btn = this.querySelector('[data-gt-add]');
      if (!btn) return;

      const pill = this.pillFor(size);
      // Sin pill (producto sin esa talla, o sin opción de talla): la guía
      // sigue siendo útil, solo sin la parte que convierte.
      if (!pill) { btn.hidden = true; return; }

      btn.hidden = false;
      btn.dataset.gtSize = size;

      // is-soldout lo mantiene el controlador de la PDP, ya cruzado con el
      // color seleccionado en este momento.
      if (pill.classList.contains('is-soldout')) {
        btn.disabled = true;
        btn.textContent = 'Talla ' + size + ' agotada';
      } else {
        btn.disabled = false;
        btn.textContent = 'Añadir talla ' + size + ' al carrito';
      }
    }

    addToCart(btn) {
      const size = btn.dataset.gtSize;
      if (!size) return;

      const pill = this.pillFor(size);
      const input = pill ? pill.querySelector('input') : null;
      if (!input || input.disabled) { this.updateCartButton(size); return; }

      const root = this.closest('.adea-pdp');
      const form = root ? root.querySelector('[data-adea-form]') : null;
      if (!form) return;

      // Selecciona la talla en el selector real y deja que el controlador de
      // la PDP resuelva la variante (respeta el color ya elegido).
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));

      btn.disabled = true;
      btn.textContent = 'Añadiendo…';

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }
  }

  customElements.define('adea-size-guide', AdeaSizeGuide);
})();
